/**
 * Client-side crash reporting (OBS1).
 *
 * Forwards browser crashes to molecule.dev's first-party analytics endpoint
 * (`POST /api/analytics/client-error`), where the server records them as
 * queryable, subsystem-tagged `error` events (subsystem `client`). Two sources:
 *   - React render crashes, via the app's ErrorBoundary `componentDidCatch`;
 *   - uncaught `window` errors + unhandled promise rejections, via the listeners
 *     {@link setupGlobalErrorReporting} attaches.
 *
 * Before this, client-side crashes reported NOWHERE — a white-screen React crash
 * left no trace the admin could query, defeating the OBS1 "catch literally
 * everything" intent on the browser half of the stack.
 *
 * This is molecule.dev-specific wiring, NOT a shared-package concern: the shared
 * `@molecule/app-analytics` bond targets a swappable provider (a no-op by default
 * here), whereas OBS1 requires the crash to land in molecule.dev's OWN queryable
 * `analyticsEvents` table. So we POST directly to the first-party endpoint rather
 * than through the analytics bond (Anti-Pattern 14: app-specific concerns belong
 * in the app, not the package).
 *
 * @module
 */

import { getClient } from '@molecule/app-http'

import { redactUrl } from './redact-url.js'

/** Where a crash was caught — mirrors the server `ClientErrorSource` union. */
export type ClientErrorSource = 'react' | 'window.onerror' | 'unhandledrejection' | 'handled'

/** Hard cap on reports per page session — a render loop must never flood the endpoint. */
const MAX_REPORTS_PER_SESSION = 50

/** Suppress a repeat of the SAME crash signature within this window (ms). */
const DEDUP_WINDOW_MS = 10_000

/** Max message / component-stack / stack lengths sent (the server caps again as defense-in-depth). */
const MAX_MESSAGE_LEN = 10_000
const MAX_COMPONENT_STACK_LEN = 4_000
const MAX_STACK_LEN = 4_000

let reportCount = 0
const recentSignatures = new Map<string, number>()

/** Arguments for {@link reportClientError}. */
export interface ReportClientErrorArgs {
  /** Where the crash was caught. */
  source: ClientErrorSource
  /** The thrown value (Error, string, or anything). */
  error: unknown
  /** The React component stack, for `react` crashes (from `componentDidCatch`). */
  componentStack?: string
}

/**
 * What the thrown value actually WAS. A rejection reason is arbitrary — a bare
 * string, a DOM `Event`, a `DOMException`, `undefined` — and without this the
 * record cannot tell `new Error('X')` from `Promise.reject('X')`, since both
 * flatten to the same `{ name: 'Error', message: 'X' }`. That ambiguity is not
 * hypothetical: it is why four production `unhandledrejection` events reading
 * `Error / "Rejected"` could not be traced to any code we ship.
 *
 * @param error - The thrown/rejected value.
 * @returns `'Error'`, a constructor name (`'DOMException'`, `'Event'`), or the
 *   primitive `typeof` (`'string'`, `'undefined'`, …).
 */
function kindOf(error: unknown): string {
  if (error instanceof Error) return 'Error'
  if (error === null) return 'null'
  if (typeof error === 'object') {
    const ctor = (error as { constructor?: { name?: string } }).constructor
    return ctor?.name || 'object'
  }
  return typeof error
}

/**
 * Normalize an arbitrary thrown value into a stable report.
 *
 * `stack` is the whole point of the exercise: a name and a message describe
 * WHAT happened but never WHERE, so an error thrown by an injected third-party
 * script (an extension running in the page context, an automation harness) is
 * indistinguishable from one of ours. The stack's frames answer that, and it is
 * the single field whose absence made the production events undiagnosable.
 *
 * @param error - The thrown/rejected value.
 * @returns The normalized name, message, kind, and stack (when one exists).
 */
function describe(error: unknown): {
  name: string
  message: string
  kind: string
  stack?: string
} {
  const kind = kindOf(error)
  if (error instanceof Error) {
    return {
      name: error.name || 'Error',
      message: error.message || String(error),
      kind,
      ...(typeof error.stack === 'string' && error.stack ? { stack: error.stack } : {}),
    }
  }
  if (typeof error === 'string') return { name: 'Error', message: error, kind }
  return { name: typeof error, message: String(error), kind }
}

/**
 * Report a browser crash to the first-party analytics endpoint. Fire-and-forget,
 * deduplicated by `source|name|message` within a short window, and hard-capped
 * per page session. Never throws and never rejects — crash reporting must not be
 * able to crash the app or re-enter the global error/rejection handlers.
 *
 * @param args - The crash details (origin, the thrown value, optional React stack).
 */
export function reportClientError(args: ReportClientErrorArgs): void {
  try {
    if (reportCount >= MAX_REPORTS_PER_SESSION) return

    const { name, message, kind, stack } = describe(args.error)
    const signature = `${args.source}|${name}|${message}`
    const now = Date.now()
    const last = recentSignatures.get(signature)
    if (last !== undefined && now - last < DEDUP_WINDOW_MS) return
    recentSignatures.set(signature, now)
    reportCount++

    void getClient()
      .post('/analytics/client-error', {
        source: args.source,
        name,
        kind,
        message: message.slice(0, MAX_MESSAGE_LEN),
        ...(stack ? { stack: stack.slice(0, MAX_STACK_LEN) } : {}),
        ...(args.componentStack
          ? { componentStack: args.componentStack.slice(0, MAX_COMPONENT_STACK_LEN) }
          : {}),
        // Redact one-time credentials (OAuth code/state, reset/verify tokens)
        // before the URL leaves the browser — audit Low, 2026-09-17.
        ...(typeof window !== 'undefined' ? { url: redactUrl(window.location.href) } : {}),
      })
      .catch(() => {
        // Best-effort: a failed crash report stays silent on purpose — logging or
        // rethrowing here could re-enter the window error / unhandledrejection
        // listeners and loop. The server-side error event is the source of truth.
      })
  } catch (_error) {
    // reportClientError must never throw (it runs inside error handlers); swallow.
  }
}

/**
 * Report a failure that WAS caught and shown to the user, in a flow whose
 * breakage is business-critical (checkout, signup, deploy). A handled failure
 * never reaches the global listeners, so without this it leaves no server-side
 * trace at all — which is how a broken prod upgrade button went unnoticed.
 * The flow name prefixes the message so events group by flow in analytics.
 *
 * @param flow - Short kebab-case flow name (e.g. `plan-upgrade`).
 * @param error - The caught value (Error, string, or anything).
 */
export function reportHandledFailure(flow: string, error: unknown): void {
  const { name, message } = describe(error)
  reportClientError({ source: 'handled', error: new Error(`[${flow}] ${name}: ${message}`) })
}

let teardown: (() => void) | undefined

/**
 * Attach global browser-crash listeners — `window` `error` (uncaught exceptions +
 * resource errors that bubble) and `unhandledrejection` (dropped promise
 * rejections) — that forward to {@link reportClientError}. Idempotent: a second
 * call removes the previous listeners first. No-op outside a browser.
 *
 * @returns A cleanup function that removes the listeners.
 */
export function setupGlobalErrorReporting(): () => void {
  if (typeof window === 'undefined') return () => {}
  teardown?.()

  const onError = (event: ErrorEvent): void => {
    reportClientError({
      source: 'window.onerror',
      error: event.error ?? new Error(event.message || 'Uncaught error'),
    })
  }
  const onRejection = (event: PromiseRejectionEvent): void => {
    reportClientError({ source: 'unhandledrejection', error: event.reason })
  }

  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)

  teardown = (): void => {
    window.removeEventListener('error', onError)
    window.removeEventListener('unhandledrejection', onRejection)
    teardown = undefined
  }
  return teardown
}
